'use client';

import * as React from 'react';
import { Award } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '@workspace/ui/lib/utils';

import { PERSONAS } from '~/lib/landing-content';

/**
 * The search showcase. A prompt types itself in a calm surface card, then
 * the people it would find pop in one by one around it. Cycles through
 * three prompts, fresh people each time.
 *
 * Person cards are recreated from the "Prospect Review" Claude Design
 * handoff (Jun 11): photo avatar, name + award on top matches,
 * role · company, a mixed-signal evidence line, the supporting reason,
 * and a fit footer (fit pill + "96 match · 7 mutual").
 * ALL personas + companies are fictional (the design's sample data used real
 * people and companies; renamed Jun 11 per Abu). Faces are stock avatars.
 *
 * NOTE: prompt 1's "raised in the last 90 days" clause is the funding
 * trigger (GATED per the claim map) — kept per Abu; cut the clause here if
 * funding slips the launch gate. Everything else is live-status.
 */

type Prompt = {
  prefix: string;
  highlights: { text: string; tail?: string }[];
  trail?: string;
};

type Person = {
  id: string;
  name: string;
  role: string;
  company: string;
  img: string | null;
  fit: 'excellent' | 'good';
  score: number;
  mutuals: number;
  kind: 'engagement' | 'company' | 'movement' | 'crm' | 'sales';
  signalLabel: string;
  signalContext: string;
  when: string;
  recent: boolean;
  snippet: string;
};

type ShowcaseSet = {
  mode: 'Prospecting' | 'Account intelligence' | 'Audience research';
  scenario: string;
  prompt: Prompt;
  people: [Person, Person, Person];
};

export const SHOWCASE: ShowcaseSet[] = [
  {
    mode: 'Prospecting',
    scenario: 'Funding + first GTM hires',
    prompt: {
      prefix: 'Series A fintech founders in NYC who ',
      highlights: [
        { text: 'raised in the last 90 days', tail: ' and posted about ' },
        { text: 'hiring GTM' }
      ],
      trail: '.'
    },
    people: [
      {
        id: 'devon',
        name: 'Devon Park',
        role: 'Founder & CEO',
        company: 'Northwind',
        img: 'https://i.pravatar.cc/120?img=14',
        fit: 'excellent',
        score: 96,
        mutuals: 7,
        kind: 'company',
        signalLabel: 'Funding + hiring',
        signalContext: 'Series A · first GTM hire',
        when: '2w ago',
        recent: false,
        snippet:
          'Northwind raised a Series A and opened its first go-to-market leadership role.'
      },
      {
        id: 'maya',
        name: 'Maya Chen',
        role: 'Founder & CEO',
        company: 'Ledgerly',
        img: 'https://i.pravatar.cc/120?img=47',
        fit: 'excellent',
        score: 94,
        mutuals: 14,
        kind: 'crm',
        signalLabel: 'CRM context',
        signalContext: 'Closed-lost · re-opened',
        when: '8d ago',
        recent: false,
        snippet:
          'A previous evaluation ended without a decision; the account is now showing fresh activity.'
      },
      {
        id: 'lin',
        name: 'Lin Park',
        role: 'Founder',
        company: 'Crative',
        img: 'https://i.pravatar.cc/120?img=38',
        fit: 'good',
        score: 89,
        mutuals: 3,
        kind: 'movement',
        signalLabel: 'People movement',
        signalContext: 'New founder role',
        when: '5d ago',
        recent: true,
        snippet:
          'Lin moved into a founder role while the company began building its first outbound motion.'
      }
    ]
  },
  {
    mode: 'Prospecting',
    scenario: 'New revenue leaders',
    prompt: {
      prefix: 'New revenue leaders at Series B software companies who are ',
      highlights: [
        { text: 'building an outbound team', tail: ' and ' },
        { text: 'hiring sales operations' }
      ],
      trail: '.'
    },
    people: [
      {
        id: 'amara',
        name: 'Amara Okafor',
        role: 'VP Revenue',
        company: 'Cedar Labs',
        img: 'https://i.pravatar.cc/120?img=49',
        fit: 'excellent',
        score: 97,
        mutuals: 9,
        kind: 'movement',
        signalLabel: 'Leadership change',
        signalContext: 'Joined 41d ago',
        when: '4d ago',
        recent: true,
        snippet:
          'Amara joined to build revenue and has opened the company’s first sales operations role.'
      },
      {
        id: 'mateo',
        name: 'Mateo Silva',
        role: 'Chief Revenue Officer',
        company: 'Flint Commerce',
        img: 'https://i.pravatar.cc/120?img=11',
        fit: 'excellent',
        score: 93,
        mutuals: 6,
        kind: 'company',
        signalLabel: 'Team build',
        signalContext: '6 open GTM roles',
        when: '1w ago',
        recent: false,
        snippet:
          'Flint Commerce is hiring across sales development, enablement, and revenue operations.'
      },
      {
        id: 'yuki',
        name: 'Yuki Tanaka',
        role: 'VP Growth',
        company: 'Paperplane',
        img: 'https://i.pravatar.cc/120?img=32',
        fit: 'good',
        score: 88,
        mutuals: 4,
        kind: 'engagement',
        signalLabel: 'Market conversation',
        signalContext: 'Outbound benchmarks',
        when: '3d ago',
        recent: true,
        snippet:
          'Yuki has been comparing outbound benchmarks while Paperplane expands into North America.'
      }
    ]
  },
  {
    mode: 'Prospecting',
    scenario: 'Ecosystem expansion',
    prompt: {
      prefix: 'Partnership leaders at vertical software companies that ',
      highlights: [
        { text: 'launched an integration marketplace', tail: ' or ' },
        { text: 'expanded their partner program' }
      ],
      trail: ' this quarter.'
    },
    people: [
      {
        id: 'leila',
        name: 'Leila Haddad',
        role: 'VP Partnerships',
        company: 'Arcwell',
        img: 'https://i.pravatar.cc/120?img=44',
        fit: 'excellent',
        score: 95,
        mutuals: 12,
        kind: 'company',
        signalLabel: 'Partner program',
        signalContext: 'Marketplace launched',
        when: '6d ago',
        recent: true,
        snippet:
          'Arcwell launched a marketplace and published its first technology-partner requirements.'
      },
      {
        id: 'kwame',
        name: 'Kwame Mensah',
        role: 'Director, Ecosystems',
        company: 'RelayForge',
        img: 'https://i.pravatar.cc/120?img=13',
        fit: 'excellent',
        score: 92,
        mutuals: 8,
        kind: 'movement',
        signalLabel: 'New mandate',
        signalContext: 'First ecosystem leader',
        when: '12d ago',
        recent: false,
        snippet:
          'Kwame joined as RelayForge’s first ecosystem leader as the company added integration roles.'
      },
      {
        id: 'sofia',
        name: 'Sofía Alvarez',
        role: 'Head of Alliances',
        company: 'Juniper Lane',
        img: 'https://i.pravatar.cc/120?img=9',
        fit: 'good',
        score: 87,
        mutuals: 5,
        kind: 'engagement',
        signalLabel: 'Partner activity',
        signalContext: '3 launch discussions',
        when: '2d ago',
        recent: true,
        snippet:
          'Sofía is discussing co-selling and implementation partners ahead of a new product launch.'
      }
    ]
  },
  {
    mode: 'Prospecting',
    scenario: 'Specialist talent',
    prompt: {
      prefix: 'AI infrastructure engineers in Europe who ',
      highlights: [
        { text: 'maintain evaluation tooling', tail: ', ' },
        { text: 'contribute to open source', tail: ', and ' },
        { text: 'changed roles this year' }
      ],
      trail: '.'
    },
    people: [
      {
        id: 'anika',
        name: 'Anika Rao',
        role: 'Staff ML Infrastructure Engineer',
        company: 'Vectorlane',
        img: 'https://i.pravatar.cc/120?img=48',
        fit: 'excellent',
        score: 96,
        mutuals: 3,
        kind: 'engagement',
        signalLabel: 'Open source',
        signalContext: 'Evaluation maintainer',
        when: '5d ago',
        recent: true,
        snippet:
          'Anika maintains an evaluation toolkit and recently published guidance on production testing.'
      },
      {
        id: 'omar',
        name: 'Omar El-Sayed',
        role: 'ML Platform Lead',
        company: 'Koru Systems',
        img: 'https://i.pravatar.cc/120?img=15',
        fit: 'excellent',
        score: 91,
        mutuals: 7,
        kind: 'movement',
        signalLabel: 'Role change',
        signalContext: 'Joined this quarter',
        when: '9d ago',
        recent: false,
        snippet:
          'Omar moved into a platform leadership role after contributing to two model-serving projects.'
      },
      {
        id: 'hana',
        name: 'Hana Moreau',
        role: 'Principal AI Engineer',
        company: 'Hearth Computing',
        img: 'https://i.pravatar.cc/120?img=23',
        fit: 'good',
        score: 86,
        mutuals: 2,
        kind: 'company',
        signalLabel: 'Technical footprint',
        signalContext: 'New evaluation stack',
        when: '2w ago',
        recent: false,
        snippet:
          'Hearth Computing added evaluation infrastructure while Hana began leading its reliability work.'
      }
    ]
  },
  {
    mode: 'Account intelligence',
    scenario: 'Named account change',
    prompt: {
      prefix: 'Show what changed at ',
      highlights: [
        { text: 'Quillstack', tail: ' and ' },
        { text: 'which people matter' }
      ],
      trail: ' this week.'
    },
    people: [
      {
        id: 'marcus',
        name: 'Marcus Hale',
        role: 'Chief Growth Officer',
        company: 'Quillstack',
        img: 'https://i.pravatar.cc/120?img=12',
        fit: 'excellent',
        score: 96,
        mutuals: 7,
        kind: 'engagement',
        signalLabel: 'Topic engagement',
        signalContext: 'Competitor + category',
        when: '24d ago',
        recent: false,
        snippet:
          'Marcus engaged with a competitor launch and a category playbook on pipeline discipline.'
      },
      {
        id: 'theo',
        name: 'Theo Brandt',
        role: 'Head of Sales',
        company: 'Quillstack',
        img: 'https://i.pravatar.cc/120?img=33',
        fit: 'excellent',
        score: 92,
        mutuals: 4,
        kind: 'sales',
        signalLabel: 'Sales activity',
        signalContext: 'Researched by 2 reps',
        when: '6d ago',
        recent: true,
        snippet:
          'Two teammates researched Theo and Quillstack during the same week.'
      },
      {
        id: 'noor',
        name: 'Noor Saleh',
        role: 'Founding Revenue Operations',
        company: 'Quillstack',
        img: null,
        fit: 'good',
        score: 88,
        mutuals: 2,
        kind: 'company',
        signalLabel: 'Hiring cluster',
        signalContext: '4 revenue roles',
        when: '11d ago',
        recent: false,
        snippet:
          'Quillstack opened four revenue roles while its go-to-market leadership team was changing.'
      }
    ]
  },
  {
    mode: 'Audience research',
    scenario: 'Market attention',
    prompt: {
      prefix: 'What are fintech security leaders ',
      highlights: [{ text: 'paying attention to in cloud security' }],
      trail: ' right now?'
    },
    people: [
      {
        id: 'priya',
        name: 'Priya Nair',
        role: 'Head of Security',
        company: 'Tributary',
        img: 'https://i.pravatar.cc/120?img=45',
        fit: 'excellent',
        score: 96,
        mutuals: 5,
        kind: 'engagement',
        signalLabel: 'Competitor engagement',
        signalContext: '2 rival vendors',
        when: '3d ago',
        recent: true,
        snippet:
          'Priya engaged with two competing cloud-security vendors during a SOC 2 project.'
      },
      {
        id: 'elias',
        name: 'Elias Trent',
        role: 'Security Lead',
        company: 'Brightvault',
        img: 'https://i.pravatar.cc/120?img=68',
        fit: 'good',
        score: 91,
        mutuals: 3,
        kind: 'engagement',
        signalLabel: 'Market conversation',
        signalContext: 'Evaluation question',
        when: '6d ago',
        recent: true,
        snippet:
          'Elias asked how a cloud-security product handles ephemeral environments during an evaluation.'
      },
      {
        id: 'dana',
        name: 'Dana Kim',
        role: 'VP Engineering',
        company: 'Finchpay',
        img: 'https://i.pravatar.cc/120?img=16',
        fit: 'good',
        score: 88,
        mutuals: 2,
        kind: 'engagement',
        signalLabel: 'Audience theme',
        signalContext: 'Breach prevention',
        when: '1w ago',
        recent: false,
        snippet:
          'Dana repeatedly engaged with breach postmortems and prevention guidance.'
      }
    ]
  }
];

// The card the final CTA closes on.
const audienceShowcase = SHOWCASE.find(
  (set) => set.mode === 'Audience research'
);
export const FEATURED_PERSON: Person = (audienceShowcase ?? SHOWCASE[0]!)
  .people[0];
export type { Person };

/* Timing (ms) */
const TYPE_MS = 24;
const AFTER_TYPE_MS = 500;
const CARD_GAP_MS = 620;
const HOLD_MS = 3400;
const EXIT_MS = 420;
const BETWEEN_MS = 380;

type ExampleShortlistProps = {
  mode?: ShowcaseSet['mode'];
  showPersonas?: boolean;
  embedded?: boolean;
  cycleModeExamples?: boolean;
};

function promptFullText(p: Prompt): string {
  return (
    p.prefix +
    p.highlights.map((h) => h.text + (h.tail ?? '')).join('') +
    (p.trail ?? '')
  );
}

export function ExampleShortlist({
  mode,
  showPersonas = true,
  embedded = false,
  cycleModeExamples = false
}: ExampleShortlistProps = {}): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;

  const [setIdx, setSetIdx] = React.useState(0);
  const [typedChars, setTypedChars] = React.useState(0);
  const [visibleCount, setVisibleCount] = React.useState(0);
  const [cycleId, setCycleId] = React.useState(0);

  const modeIndices = React.useMemo(
    () =>
      mode
        ? SHOWCASE.reduce<number[]>((indices, set, index) => {
            if (set.mode === mode) indices.push(index);
            return indices;
          }, [])
        : [],
    [mode]
  );
  const cyclesWithinMode = Boolean(
    mode && cycleModeExamples && modeIndices.length > 1
  );
  const fixedIndex = mode && !cyclesWithinMode ? (modeIndices[0] ?? 0) : null;
  const currentScenarioSlot = cyclesWithinMode
    ? setIdx % modeIndices.length
    : null;
  const currentIndex = cyclesWithinMode
    ? modeIndices[currentScenarioSlot!]!
    : (fixedIndex ?? setIdx);
  const current = SHOWCASE[currentIndex];
  const fullText = promptFullText(current.prompt);

  React.useEffect(() => {
    if (reduced) {
      setTypedChars(promptFullText(SHOWCASE[currentIndex].prompt).length);
      setVisibleCount(3);
      return;
    }

    let mounted = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const full = promptFullText(SHOWCASE[currentIndex].prompt);

    setTypedChars(0);
    setVisibleCount(0);

    // Phase 1: the prompt types itself.
    const typeId = setInterval(() => {
      if (!mounted) return;
      setTypedChars((c) => {
        if (c >= full.length) {
          clearInterval(typeId);
          return c;
        }
        return c + 1;
      });
    }, TYPE_MS);

    // Phase 2: people pop in one by one.
    const typeDone = full.length * TYPE_MS + AFTER_TYPE_MS;
    for (let i = 0; i < 3; i += 1) {
      timers.push(
        setTimeout(
          () => mounted && setVisibleCount(i + 1),
          typeDone + i * CARD_GAP_MS
        )
      );
    }

    // A fixed-mode preview animates in once and remains available for review.
    if (fixedIndex !== null) {
      return () => {
        mounted = false;
        clearInterval(typeId);
        timers.forEach(clearTimeout);
      };
    }

    // Phase 3: hold, clear, advance to the next prompt.
    const lastCardAt = typeDone + 2 * CARD_GAP_MS;
    timers.push(
      setTimeout(() => {
        if (!mounted) return;
        setVisibleCount(0);
        timers.push(
          setTimeout(() => {
            if (!mounted) return;
            setSetIdx((i) =>
              cyclesWithinMode
                ? (i + 1) % modeIndices.length
                : (i + 1) % SHOWCASE.length
            );
            setCycleId((c) => c + 1);
          }, EXIT_MS + BETWEEN_MS)
        );
      }, lastCardAt + HOLD_MS)
    );

    return () => {
      mounted = false;
      clearInterval(typeId);
      timers.forEach(clearTimeout);
    };
  }, [currentIndex, cyclesWithinMode, fixedIndex, modeIndices.length, reduced]);

  const isTyping = typedChars < fullText.length;

  return (
    <section
      className={cn(
        'relative overflow-hidden',
        embedded
          ? 'bg-transparent py-0'
          : 'bg-[color:var(--paper)] py-10 md:py-14'
      )}
    >
      <div
        className={cn(
          'w-full mx-auto',
          embedded
            ? 'max-w-none px-0'
            : 'max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16'
        )}
      >
        {/* Built for [roles] → then what those roles see (the demo below). */}
        {showPersonas && (
          <motion.div
            className="flex flex-wrap items-center justify-center gap-2 mb-6 md:mb-8"
            initial={{ opacity: 0, y: reduced ? 0 : 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45 }}
          >
            <span className="font-mono text-[10.5px] tracking-[0.1em] uppercase font-medium text-[color:var(--ink-4)] mr-1">
              {PERSONAS.label}
            </span>
            {/* The v2 hero's persona-chip treatment (Abu, Jun 11): soft mint
                fill + teal border, matching the backers pill. */}
            {PERSONAS.list.map((persona) => (
              <span
                key={persona}
                className="rounded-full border border-teal-200/70 dark:border-teal-800/70 bg-teal-50/80 dark:bg-teal-950/60 px-3 py-1.5 text-[12px] font-medium text-foreground/90 whitespace-nowrap"
              >
                {persona}
              </span>
            ))}
          </motion.div>
        )}

        {cyclesWithinMode && (
          <nav
            aria-label={`${mode} examples`}
            className="mx-auto mb-4 flex max-w-[680px] gap-5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {modeIndices.map((showcaseIndex, slot) => {
              const scenario = SHOWCASE[showcaseIndex]!;
              const active = slot === currentScenarioSlot;

              return (
                <button
                  key={scenario.scenario}
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  onClick={() => {
                    if (active) return;
                    setVisibleCount(0);
                    setSetIdx(slot);
                    setCycleId((cycle) => cycle + 1);
                  }}
                  className={cn(
                    'relative shrink-0 pb-2 font-mono text-[9px] font-medium uppercase tracking-[0.09em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
                    active
                      ? 'text-[color:var(--accent-darker)]'
                      : 'text-[color:var(--ink-5)] hover:text-[color:var(--ink-3)]'
                  )}
                >
                  <span className="mr-1.5 tabular-nums">
                    {String(slot + 1).padStart(2, '0')}
                  </span>
                  {scenario.scenario}
                  {active && (
                    <motion.span
                      layoutId="prospecting-example-position"
                      className="absolute inset-x-0 bottom-0 h-px bg-teal-500"
                      transition={{
                        type: 'spring',
                        stiffness: 360,
                        damping: 34
                      }}
                    />
                  )}
                </button>
              );
            })}
          </nav>
        )}

        {/* The prompt surface */}
        <motion.div
          className="mx-auto max-w-[680px]"
          initial={{ opacity: 0, y: reduced ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <div className="surface-card rounded-2xl bg-[color:var(--paper)] border border-[color:var(--hairline)] p-5">
            <div className="flex items-center justify-between mb-3">
              {!cyclesWithinMode && (
                <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase font-medium text-[color:var(--ink-4)]">
                  {current.scenario}
                </span>
              )}
              <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.1em] uppercase text-[color:var(--ink-4)]">
                <span className="relative flex h-1.5 w-1.5">
                  {!reduced && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500/60" />
                  )}
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-teal-500" />
                </span>
                Listening
              </span>
            </div>
            {/* The prompt sits directly on the card: no inset box. */}
            <div
              className="h-[128px] overflow-hidden px-0.5 font-mono text-[15px] leading-relaxed text-[color:var(--ink)] md:h-auto md:min-h-[68px] md:overflow-visible"
              aria-live="polite"
            >
              <TypedPrompt
                prompt={current.prompt}
                typedChars={typedChars}
                isTyping={isTyping}
              />
            </div>
          </div>
        </motion.div>

        {/* The people it found, popping in around the prompt.
            Reserve the FULL height of the stack so the grid never collapses
            between cycles (the cards mount/unmount as the demo loops). The
            tallest authored mobile stack is ~728px; a fixed 800px stage leaves
            enough breathing room for the card entrance transforms without
            moving any section below it. md+ lays the three out in a single row,
            so the original one-card reserve is enough. */}
        <div
          className={cn(
            'mx-auto max-w-[1080px] mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-start',
            fixedIndex === null && 'h-[800px] md:h-auto md:min-h-[224px]'
          )}
        >
          <AnimatePresence>
            {current.people.slice(0, visibleCount).map((person, i) => (
              <PopCard
                key={`${cycleId}-${person.id}`}
                person={person}
                index={i}
                reduced={reduced}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────
   Typed prompt with mint marker highlights (shared brand pattern).
   ─────────────────────────────────────────────────────────────── */

const MARK_STYLE: React.CSSProperties = {
  color: 'var(--accent-darker)',
  padding: '0.1em 0.3em',
  backgroundImage:
    'linear-gradient(100deg, var(--mint) 0%, var(--mint-2) 45%, var(--mint) 100%)',
  borderRadius: '0.5em 0.15em 0.4em 0.2em / 0.2em 0.4em 0.15em 0.5em',
  boxShadow:
    'inset 0 -0.12em 0 color-mix(in srgb, var(--accent-darker) 8%, transparent)',
  WebkitBoxDecorationBreak: 'clone',
  boxDecorationBreak: 'clone'
};

export function TypedPrompt({
  prompt,
  typedChars,
  isTyping
}: {
  prompt: Prompt;
  typedChars: number;
  isTyping: boolean;
}): React.JSX.Element {
  let cursor = 0;
  const out: React.ReactNode[] = [];

  const pushPlain = (s: string, key: string): void => {
    const remaining = typedChars - cursor;
    if (remaining > 0) out.push(<span key={key}>{s.slice(0, remaining)}</span>);
  };
  const pushHighlight = (s: string, key: string): void => {
    const remaining = typedChars - cursor;
    if (remaining > 0)
      out.push(
        <mark
          key={key}
          style={MARK_STYLE}
        >
          {s.slice(0, remaining)}
        </mark>
      );
  };

  pushPlain(prompt.prefix, 'prefix');
  cursor += prompt.prefix.length;
  prompt.highlights.forEach((h, idx) => {
    pushHighlight(h.text, `hl-${idx}`);
    cursor += h.text.length;
    if (h.tail) {
      pushPlain(h.tail, `tail-${idx}`);
      cursor += h.tail.length;
    }
  });
  if (prompt.trail) {
    pushPlain(prompt.trail, 'trail');
    cursor += prompt.trail.length;
  }

  return (
    <span>
      {out}
      {isTyping && (
        <span
          aria-hidden
          className="inline-block w-[2px] h-[1.05em] align-[-3px] bg-[color:var(--ink-3)] ml-[1px] animate-pulse"
        />
      )}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────────
   Person card (the design's evidence-first row, folded into a card).
   ─────────────────────────────────────────────────────────────── */

const TILTS = [-1.2, 0.9, -0.7];

function PopCard({
  person,
  index,
  reduced
}: {
  person: Person;
  index: number;
  reduced: boolean;
}): React.JSX.Element {
  const tilt = reduced ? 0 : TILTS[index % TILTS.length];
  return (
    <motion.div
      initial={
        reduced
          ? { opacity: 1 }
          : { opacity: 0, y: 24, scale: 0.94, rotate: tilt * 2 }
      }
      animate={{ opacity: 1, y: 0, scale: 1, rotate: tilt }}
      exit={
        reduced
          ? { opacity: 0 }
          : { opacity: 0, y: -10, scale: 0.97, transition: { duration: 0.35 } }
      }
      transition={{ type: 'spring', stiffness: 320, damping: 24 }}
    >
      <PersonCard
        person={person}
        showMatchScore={false}
      />
    </motion.div>
  );
}

const KIND_META: Record<Person['kind'], { className: string }> = {
  engagement: { className: 'text-[color:var(--accent-deep)]' },
  company: { className: 'text-[color:var(--ink-3)]' },
  movement: { className: 'text-[color:var(--ink-3)]' },
  crm: { className: 'text-amber-700' },
  sales: { className: 'text-[color:var(--accent-darker)]' }
};

export function PersonCard({
  person,
  showMatchScore = true
}: {
  person: Person;
  showMatchScore?: boolean;
}): React.JSX.Element {
  const initials = person.name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('');
  const kind = KIND_META[person.kind];

  return (
    <div className="surface-card rounded-2xl bg-[color:var(--paper)] border border-[color:var(--hairline-2)] p-4 text-left shadow-sm">
      {/* Person */}
      <div className="flex items-center gap-3">
        <span
          className="h-10 w-10 rounded-full overflow-hidden shrink-0 flex items-center justify-center font-semibold text-[13px]"
          style={{ background: 'var(--mint)', color: 'var(--accent-darker)' }}
        >
          {person.img ? (
            <img
              src={person.img}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            initials
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-[color:var(--ink)] leading-tight">
            <span className="truncate">{person.name}</span>
            {person.score >= 94 && (
              <Award
                aria-label="Top match"
                className="w-3.5 h-3.5 text-amber-600 shrink-0"
              />
            )}
          </div>
          <div className="mt-0.5 text-[12.5px] text-[color:var(--ink-4)] truncate">
            {person.role}
            <span className="px-1 text-[color:var(--ink-5)]">·</span>
            <span className="font-medium text-[color:var(--ink-3)]">
              {person.company}
            </span>
          </div>
        </div>
      </div>

      {/* Mixed-signal evidence */}
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] tracking-[0.06em] uppercase text-[color:var(--ink-4)]">
        <span className={cn('font-semibold', kind.className)}>
          {person.signalLabel}
        </span>
        <span
          aria-hidden
          className="text-[color:var(--ink-5)]"
        >
          ·
        </span>
        <span>{person.signalContext}</span>
        <span
          aria-hidden
          className="text-[color:var(--ink-5)]"
        >
          ·
        </span>
        {person.recent ? (
          <span
            className="inline-flex items-center px-1.5 py-px rounded-full font-semibold normal-case tracking-normal"
            style={{
              background: 'color-mix(in srgb, #00C896 10%, transparent)',
              color: 'var(--accent-darker)'
            }}
          >
            {person.when}
          </span>
        ) : (
          <span>{person.when}</span>
        )}
      </div>
      <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[color:var(--ink-3)] m-0 line-clamp-3">
        {person.snippet}
      </p>

      {/* Fit footer */}
      <div className="mt-3 pt-3 border-t border-[color:var(--hairline-2)] flex items-center justify-between gap-2">
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-[3px] rounded-full text-[11px] font-medium border',
            person.fit === 'excellent'
              ? 'bg-[color:var(--mint)] text-[color:var(--accent-darker)] border-teal-500/25'
              : 'text-[color:var(--accent-darker)] border-[color:var(--hairline-2)]'
          )}
          style={
            person.fit === 'excellent'
              ? undefined
              : { background: 'color-mix(in srgb, #00C896 4%, var(--paper))' }
          }
        >
          {person.fit === 'excellent' ? 'Excellent fit' : 'Good fit'}
        </span>
        <span className="font-mono text-[10.5px] tracking-[0.02em] text-[color:var(--ink-4)] tabular-nums">
          {showMatchScore && <>{person.score} match · </>}
          {person.mutuals} mutual
        </span>
      </div>
    </div>
  );
}
